import { useCallback, useEffect, useState } from 'react';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { createJobRole, fetchJobRoles, updateJobRole } from './competenceApi';
import { formatCompetencies } from './competenceUtils';
import type { CompetencyItem, JobRole } from './types';

interface JobRolesTabProps {
  canEdit: boolean;
}

const emptyCompetency = (): CompetencyItem => ({ name: '', level: 1 });

export function JobRolesTab({ canEdit }: JobRolesTabProps) {
  const { confirm } = useConfirmDialog();
  const [roles, setRoles] = useState<JobRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<JobRole | null>(null);
  const [name, setName] = useState('');
  const [competencies, setCompetencies] = useState<CompetencyItem[]>([emptyCompetency()]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJobRoles(showInactive ? {} : { active: true });
      setRoles(data);
    } catch {
      setError('No se pudieron cargar los perfiles de puesto.');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setFormOpen(true);
    setEditing(null);
    setName('');
    setCompetencies([emptyCompetency()]);
  };

  const openEdit = (role: JobRole) => {
    setFormOpen(true);
    setEditing(role);
    setName(role.name);
    setCompetencies(
      role.requiredCompetencies.length > 0 ? [...role.requiredCompetencies] : [emptyCompetency()],
    );
  };

  const handleSave = async () => {
    const items = competencies.filter((c) => c.name.trim());
    if (!name.trim() || items.length === 0) return;
    setSaving(true);
    try {
      if (editing) {
        await updateJobRole(editing.id, { name: name.trim(), requiredCompetencies: items });
      } else {
        await createJobRole({ name: name.trim(), requiredCompetencies: items });
      }
      setFormOpen(false);
      setEditing(null);
      setName('');
      setCompetencies([emptyCompetency()]);
      await load();
    } catch {
      setError('No se pudo guardar el perfil de puesto.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (role: JobRole) => {
    const ok = await confirm({
      title: 'Dar de baja',
      message: `¿Dar de baja el puesto "${role.name}"?`,
      confirmText: 'Dar de baja',
      variant: 'danger',
    });
    if (!ok) return;
    await updateJobRole(role.id, { active: false });
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Mostrar inactivos
        </label>
        {canEdit && (
          <button
            type="button"
            className="text-sm rounded bg-primary text-primary-foreground px-3 py-1.5"
            onClick={openCreate}
          >
            Nuevo puesto
          </button>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : (
        <div className="overflow-x-auto border rounded-md">
          <table className="min-w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2">Puesto</th>
                <th className="text-left p-2">Competencias requeridas</th>
                <th className="text-left p-2">Estado</th>
                {canEdit && <th className="text-right p-2">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id} className="border-t border-border">
                  <td className="p-2 font-medium">{role.name}</td>
                  <td className="p-2 text-muted-foreground">
                    {formatCompetencies(role.requiredCompetencies)}
                  </td>
                  <td className="p-2">{role.active ? 'Activo' : 'Inactivo'}</td>
                  {canEdit && (
                    <td className="p-2 text-right space-x-2">
                      <button type="button" className="text-primary text-xs" onClick={() => openEdit(role)}>
                        Editar
                      </button>
                      {role.active && (
                        <button
                          type="button"
                          className="text-destructive text-xs"
                          onClick={() => void handleDeactivate(role)}
                        >
                          Baja
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {roles.length === 0 && (
                <tr>
                  <td colSpan={canEdit ? 4 : 3} className="p-4 text-center text-muted-foreground">
                    Sin perfiles de puesto registrados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {canEdit && formOpen && (
        <div className="border rounded-md p-4 space-y-3 bg-muted/20">
          <h3 className="font-medium">{editing ? 'Editar puesto' : 'Nuevo puesto'}</h3>
          <input
            className="w-full border rounded px-2 py-1.5 text-sm bg-background"
            placeholder="Nombre del puesto"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Competencias requeridas (nombre y nivel 0–10)</p>
            {competencies.map((item, idx) => (
              <div key={idx} className="flex gap-2">
                <input
                  className="flex-1 border rounded px-2 py-1 text-sm bg-background"
                  placeholder="Competencia"
                  value={item.name}
                  onChange={(e) => {
                    const next = [...competencies];
                    next[idx] = { ...next[idx], name: e.target.value };
                    setCompetencies(next);
                  }}
                />
                <input
                  type="number"
                  min={0}
                  max={10}
                  className="w-20 border rounded px-2 py-1 text-sm bg-background"
                  value={item.level}
                  placeholder="Nivel"
                  onChange={(e) => {
                    const next = [...competencies];
                    next[idx] = { ...next[idx], level: Number(e.target.value) };
                    setCompetencies(next);
                  }}
                />
                <button
                  type="button"
                  className="text-xs text-muted-foreground"
                  onClick={() => setCompetencies(competencies.filter((_, i) => i !== idx))}
                >
                  Quitar
                </button>
              </div>
            ))}
            <button
              type="button"
              className="text-xs text-primary"
              onClick={() => setCompetencies([...competencies, emptyCompetency()])}
            >
              + Agregar competencia
            </button>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={saving}
              className="text-sm rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
              onClick={() => void handleSave()}
            >
              Guardar
            </button>
            <button
              type="button"
              className="text-sm rounded border px-3 py-1.5"
              onClick={() => {
                setFormOpen(false);
                setEditing(null);
                setName('');
                setCompetencies([emptyCompetency()]);
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
